//jsxhook

import {
  Column,
  FontWeight,
  Label2,
  Modal,
  PrimaryButton,
  TechnicalParagraph
} from "@sixty-six-north/ui-system"
import React from "react"
import { useTranslation } from "react-i18next"
import { Box, Divider, Grid } from "theme-ui"
import { SmallProductCard } from "./product/SmallProductCard"

export interface StoreStockInformation {
  store: string
  inStock: boolean
}

export const AvailabilityModal: React.FC<{
  sku: string
  productName: string
  productPrice: string
  productColor: string
  productSize: string
  show: boolean
  close: () => void
  addToBag: () => void
  image: string
  imageTitle: string
  stockInformation: StoreStockInformation[]
  inStockOnline: boolean
}> = ({
  productName,
  productPrice,
  productColor,
  productSize,
  show,
  close,
  addToBag,
  image,
  imageTitle,
  stockInformation,
  inStockOnline
}) => {
  const { t } = useTranslation("product")

  return (
    <Modal
      show={show && stockInformation.length > 0}
      close={close}
      title={t("storeAvailability")}
      width={[780]}
    >
      <Availability
        productName={productName}
        productPrice={productPrice}
        productColor={productColor}
        productSize={productSize}
        productImage={image}
        productImageTitle={imageTitle}
        addToBag={() => Promise.resolve(addToBag())}
        close={() => Promise.resolve(close())}
        stockInformation={stockInformation}
        inStockOnline={inStockOnline}
      />
    </Modal>
  )
}

export const Availability: React.FC<{
  productName: string
  productPrice: string
  productColor: string
  productSize: string
  productImage: string
  productImageTitle: string
  addToBag: () => Promise<void>
  close: () => Promise<void>
  stockInformation: StoreStockInformation[]
  inStockOnline: boolean
}> = ({
  productName,
  productPrice,
  productColor,
  productSize,
  productImage,
  productImageTitle,
  addToBag,
  close,
  stockInformation,
  inStockOnline
}) => {
  const { t } = useTranslation("product")
  return (
    <React.Fragment>
      <SmallProductCard
        firstRowTitle={productName}
        firstRowDescription={productPrice}
        secondRowTitle={t("product:color")}
        secondRowDescription={productColor}
        thirdRowTitle={t("checkout:size")}
        thirdRowDescription={productSize}
        productImageUrl={productImage}
        productImageTitle={productImageTitle}
      />

      <TechnicalParagraph sx={{ my: 24 }}>
        {t("stockUpdateFrequency", {
          defaultValue: "Stock is updated hourly."
        })}
      </TechnicalParagraph>

      <Grid
        as="dl"
        columns={[1, null, null, "0.5fr 1fr"]}
        gap={[0, null, null, 12]}
        sx={{
          width: "100%",
          rowGap: 0
        }}
      >
        <Box
          as="dt"
          sx={{
            mb: [8, null, null, 0],
            display: ["block", null, null, "none"],
            color: "grey.1"
          }}
        >
          <Label2 sx={{ color: "black.1", fontWeight: FontWeight.textBold }}>
            {t("storeAndQuantity", { defaultValue: "Store and quantity" })}
          </Label2>
        </Box>
        <Box
          as="dt"
          sx={{ color: "grey.1", display: ["none", null, null, "inherit"] }}
        >
          <Label2 sx={{ color: "black.1", fontWeight: FontWeight.textBold }}>
            {t("store", { defaultValue: "Store" })}
          </Label2>
        </Box>
        <Box
          as="dt"
          sx={{ color: "grey.1", display: ["none", null, null, "inherit"] }}
        >
          <Label2 sx={{ color: "black.1", fontWeight: FontWeight.textBold }}>
            {t("quantity", { defaultValue: "Quantity" })}
          </Label2>
        </Box>

        {stockInformation.map((it, idx) => {
          return (
            <React.Fragment key={`spec-${idx}-${it.store}`}>
              <Box as="dt" sx={{ color: "grey.1" }}>
                <Label2 sx={{ color: "black.1" }}>{it.store}</Label2>
              </Box>
              <Box
                as="dd"
                sx={{
                  mb: [8, null, null, 0],
                  color: "black.1"
                }}
              >
                <Quantity inStock={it.inStock} />
              </Box>
            </React.Fragment>
          )
        })}
      </Grid>

      <Divider sx={{ borderColor: "grey.3", mt: 24, mb: 16 }} />

      {inStockOnline && (
        <Box sx={{ mt: 8 }}>
          <Grid
            columns={[1, "2fr 1fr"]}
            sx={{
              "& button ": {
                width: ["50%", "100%"]
              }
            }}
          >
            <Column>
              <Box sx={{ mb: 8 }}>
                <Label2>
                  {t("whyNotOrderOnline", {
                    defaultValue: "Why not order online?"
                  })}
                </Label2>
              </Box>
              <Box sx={{ mb: 8 }}>
                <Label2>
                  {t("onlineOrdersCanBeReturned", {
                    defaultValue:
                      "Online orders can be returned to stores free of charge."
                  })}
                </Label2>
              </Box>
            </Column>
            <PrimaryButton
              variant="primary"
              onClick={() => close().then(addToBag)}
            >
              {t("addToBag", { defaultValue: "Add to bag" })}
            </PrimaryButton>
          </Grid>
        </Box>
      )}
    </React.Fragment>
  )
}

export const Quantity: React.FC<{ inStock: boolean }> = ({ inStock }) => {
  const { t } = useTranslation("product")

  const { key, defaultValue } = inStock
    ? { key: "inStock", defaultValue: "In Stock" }
    : { key: "outOfStock", defaultValue: "Not in Stock" }
  return (
    <Label2 sx={{ color: inStock ? "green.0" : "red.1" }}>
      {t(key, defaultValue)}
    </Label2>
  )
}
